import { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2 } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchUsers = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { name, email, role });
      toast({ title: "Account created" });
      setName("");
      setEmail("");
      setRole("staff");
      setOpen(false);
      fetchUsers();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not create account", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/users/${id}`);
    toast({ title: "Staff account removed" });
    fetchUsers();
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await api.patch(`/users/${id}/role`, { role: newRole });
      toast({ title: "Role updated" });
      fetchUsers();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not update role", description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl text-amtm-text">Staff Accounts</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
                <UserPlus className="h-4 w-4 mr-1.5" /> Add Account
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-staff-dialog" className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="staff-name">Name</Label>
                  <Input id="staff-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="staff-name-input" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label htmlFor="staff-email">Email</Label>
                  <Input id="staff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="staff-email-input" className="mt-1.5 h-11" />
                  <p className="text-xs text-amtm-textMuted mt-1">They'll sign in with Google using this exact email - no password needed.</p>
                </div>
                <div>
                  <Label htmlFor="staff-role">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger id="staff-role" data-testid="staff-role-select" className="mt-1.5 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff" data-testid="staff-role-option-staff">Staff</SelectItem>
                      <SelectItem value="admin" data-testid="staff-role-option-admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="w-full bg-amtm-teal text-white hover:bg-amtm-tealHover" data-testid="create-staff-submit-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : (
          <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="staff-list">
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <div key={u.id} className="flex items-center justify-between p-4 gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-amtm-text truncate">{u.name}</p>
                    <p className="text-sm text-amtm-textMuted truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isSelf ? (
                      <Badge data-testid={`role-badge-${u.id}`}>{u.role}</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(newRole) => handleRoleChange(u.id, newRole)}>
                        <SelectTrigger data-testid={`role-select-${u.id}`} className="h-8 w-28 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {!isSelf && u.role !== "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-staff-${u.id}`} className="text-amtm-error">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>They will no longer be able to log in.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(u.id)} className="bg-amtm-error hover:bg-amtm-error/90">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
